<template>
  <system-frame title="Meeldetuletused" hint="Hoolduse meeldetuletused klientidele: mis on tulemas ja mis on juba saadetud.">

    <card class="mb-6">
      <div class="d-flex align-center flex-wrap">
        <div class="text-h6 mr-4">Tulemas</div>
        <v-select
          v-model="days"
          :items="dayOptions"
          label="Ajavahemik"
          hide-details
          style="max-width: 200px"
          @update:model-value="loadUpcoming"
          variant="outlined"
          density="compact"
        />
      </div>
      <p class="text-grey-darken-1 mt-3 mb-0">
        Objektid, millele süsteem saadab hooldusmeeldetuletuse valitud aja jooksul.
        Meeldetuletus saadetakse siis, kui viimasest hooldusest (kalendri kanne
        kategooriaga "Hooldus") on möödas {{windowFrom}}–{{windowTo}} päeva.
      </p>
      <p v-if="upcomingCount > upcoming.length" class="text-orange-darken-3 mb-0">
        Kokku {{upcomingCount}} objekti — näidatakse {{upcoming.length}} kõige varasemat.
      </p>
      <v-data-table
        :headers="upcomingHeaders"
        :items="upcoming"
        :loading="upcomingLoading"
        :items-per-page="10"
        no-data-text="Lähiajal meeldetuletusi ei saadeta"
        class="mt-2"
      >
        <template v-slot:[`item.lastVisit`]="{ value }">
          {{formatDate(value, 'YYYY-MM-DD')}}
        </template>
        <template v-slot:[`item.dueInDays`]="{ value }">
          <v-chip v-if="!value" color="orange" size="x-small" variant="flat">Saadetakse kohe</v-chip>
          <span v-else>{{value}} päeva pärast</span>
        </template>
        <template v-slot:[`item.channels`]="{ item }">
          <v-chip v-if="item.reminderEmail" class="mr-1" size="x-small">E-post</v-chip>
          <v-chip v-if="item.reminderSms" size="x-small">SMS</v-chip>
        </template>
        <template v-slot:[`item.address`]="{ item }">
          <router-link :to="{ name: 'object/view', params: { objectId: item.objectId } }">
            {{item.address}}
          </router-link>
        </template>
      </v-data-table>
    </card>

    <card>
      <div class="text-h6">Saadetud</div>
      <p class="text-grey-darken-1 mt-2 mb-0">
        Iga automaatne teavitus, mille süsteem on välja saatnud, koos põhjusega —
        "Hoolduse vanus" on selle hoolduse vanus päevades, mis teavituse käivitas.
      </p>
      <v-data-table-server
        :headers="logHeaders"
        :items="entries"
        :loading="loading"
        :items-length="count"
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items-per-page-options="itemsPerPageOptions"
        no-data-text="Teavitusi pole veel saadetud"
        class="mt-2"
      >
        <template v-slot:[`item.sentAt`]="{ value }">
          {{formatDate(value)}}
        </template>
        <template v-slot:[`item.address`]="{ item }">
          <router-link v-if="item.objectId" :to="{ name: 'object/view', params: { objectId: item.objectId } }">
            {{item.address || '#' + item.objectId}}
          </router-link>
          <span v-else>-</span>
        </template>
        <template v-slot:[`item.type`]="{ value }">
          {{typeLabel(value)}}
        </template>
        <template v-slot:[`item.channel`]="{ value }">
          {{value === 'SMS' ? 'SMS' : 'E-post'}}
        </template>
        <template v-slot:[`item.daysSince`]="{ value }">
          {{value === null || value === undefined ? '-' : value + ' p'}}
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <v-chip v-if="item.status === 'SENT'" color="green" size="x-small" variant="flat">Saadetud</v-chip>
          <v-tooltip v-else location="bottom">
            <template v-slot:activator="{ props }">
              <v-chip color="red" v-bind="props" size="x-small" variant="flat">Ebaõnnestus</v-chip>
            </template>
            <span>{{item.error || 'Tundmatu viga'}}</span>
          </v-tooltip>
        </template>
      </v-data-table-server>
    </card>
  </system-frame>
</template>

<script>
import SystemFrame from '@/components/SystemFrame.vue'
import api from '@/api'
import dayjs from 'dayjs'
import listMixin from '@/plugins/listMixin'

// Mirrors the `type` strings the senders pass to communication.sendEmail/sendSMS.
const TYPE_LABELS = {
  MAINTENENCE_REMINDER: 'Hooldusmeeldetuletus',
  MAINTENENCE_VISIT_REMINDER: 'Visiidi meeldetuletus'
}

export default {
  components: { SystemFrame },
  mixins: [ listMixin ],
  data () {
    return {
      listViewConf: {
        apiMethod: api.getReminderLog,
        itemKey: 'entries'
      },
      entries: [],
      upcoming: [],
      upcomingCount: 0,
      upcomingLoading: true,
      days: 30,
      windowFrom: 330,
      windowTo: 360,
      dayOptions: [
        { text: 'Järgmised 7 päeva', value: 7 },
        { text: 'Järgmised 30 päeva', value: 30 },
        { text: 'Järgmised 90 päeva', value: 90 }
      ],
      upcomingHeaders: [
        { title: 'Objekt', value: 'address', sortable: false },
        { title: 'Klient', value: 'clientName', sortable: false },
        { title: 'Viimane hooldus', value: 'lastVisit', sortable: false },
        { title: 'Vanus (p)', value: 'daysSince', sortable: false },
        { title: 'Saadetakse', value: 'dueInDays', sortable: false },
        { title: 'Kanalid', value: 'channels', sortable: false }
      ],
      logHeaders: [
        { title: 'Aeg', value: 'sentAt', sortable: false },
        { title: 'Objekt', value: 'address', sortable: false },
        { title: 'Klient', value: 'clientName', sortable: false },
        { title: 'Tüüp', value: 'type', sortable: false },
        { title: 'Kanal', value: 'channel', sortable: false },
        { title: 'Saaja', value: 'recipient', sortable: false },
        { title: 'Hoolduse vanus', value: 'daysSince', sortable: false },
        { title: 'Staatus', value: 'status', sortable: false }
      ]
    }
  },
  computed: {
    // The log endpoint caps `limit`, so the mixin's "all rows" option is dropped here.
    itemsPerPageOptions () {
      return [10, 20, 50, 100]
    }
  },
  methods: {
    formatDate (date, format = 'YYYY-MM-DD HH:mm') {
      if (!date) return '-'
      return dayjs(date).format(format)
    },
    typeLabel (type) {
      return TYPE_LABELS[type] || type
    },
    async loadUpcoming () {
      try {
        this.upcomingLoading = true
        const { entries, count } = await api.getUpcomingReminders({ days: this.days })
        this.upcoming = entries
        this.upcomingCount = count
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.upcomingLoading = false
      }
    }
  },
  mounted () {
    this.loadUpcoming()
  }
}
</script>
